import { gql } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';

const ALLOCATION_FIELDS = `
  financialProductId
  percentage
  isCashEquivalent
  assetClassId
`;

const MODEL_DETAILS_FIELDS = `
  name { ${TRANSLATED_STRING_FIELDS} }
  portfolioDescription { ${TRANSLATED_STRING_FIELDS} }
  investmentObjective { ${TRANSLATED_STRING_FIELDS} }
  liquidity { ${TRANSLATED_STRING_FIELDS} }
  portfolioDisclaimer { ${TRANSLATED_STRING_FIELDS} }
`;

/**
 * Everything a proposal holds about the change, read on both sides so a step
 * can show what is being proposed against what the household has today without
 * fetching the model portfolio a second time.
 */
const MODEL_CHANGE_PROPOSAL_FIELDS = `
  id
  state
  financialProductId
  objectType
  objectId
  targetStrategy
  currentAllocations { ${ALLOCATION_FIELDS} }
  proposedAllocations { ${ALLOCATION_FIELDS} }
  currentModelDetails { ${MODEL_DETAILS_FIELDS} }
  proposedModelDetails { ${MODEL_DETAILS_FIELDS} }
  currentAssetClassThresholds { secondaryAssetClassId min max }
  proposedAssetClassThresholds { secondaryAssetClassId min max }
  currentAssetClassBranches { assetClassId targetPercentage }
  proposedAssetClassBranches { assetClassId targetPercentage }
`;

export const CREATE_MODEL_CHANGE_PROPOSAL = gql`
  mutation createModelChangeProposal($input: CreateModelChangeProposalInput!) {
    createModelChangeProposal(input: $input) {
      modelChangeProposal {
        ${MODEL_CHANGE_PROPOSAL_FIELDS}
      }
    }
  }
`;

export const UPDATE_MODEL_CHANGE_PROPOSAL = gql`
  mutation updateModelChangeProposal($input: UpdateModelChangeProposalInput!) {
    updateModelChangeProposal(input: $input) {
      modelChangeProposal {
        ${MODEL_CHANGE_PROPOSAL_FIELDS}
      }
    }
  }
`;

/**
 * The state is what a step acts on after a transition, and the id is what keeps
 * the cache entry the other documents wrote.
 */
export const TRANSITION_MODEL_CHANGE_PROPOSAL = gql`
  mutation transitionModelChangeProposal(
    $input: TransitionModelChangeProposalInput!
  ) {
    transitionModelChangeProposal(input: $input) {
      modelChangeProposal {
        id
        state
      }
    }
  }
`;

/**
 * The proposals open against a household.
 *
 * Only one can be open at a time — product-service refuses to create a second —
 * so this is how a workflow that returns to the allocations step finds the one
 * it left behind instead of failing on create.
 */
export const FETCH_HOUSEHOLD_MODEL_CHANGE_PROPOSALS = gql`
  query fetchHouseholdModelChangeProposals(
    $input: FetchModelChangeProposalsInput!
  ) {
    fetchModelChangeProposals(input: $input) {
      totalCount
      modelChangeProposals {
        ${MODEL_CHANGE_PROPOSAL_FIELDS}
      }
    }
  }
`;
